export default {
  lang: {
    en: 'EN',
    zh: '中文',
  },

  nav: {
    products: 'Products',
    mission: 'Services',
    stories: 'Cases',
    contact: 'Contact',
    exploreProducts: 'Explore Products',
  },

  hero: {
    badge: 'Build the Future',
    title: 'From innovative ideas to AI-native products.',
    subtitle: 'AI Infra × AI Builders = 10× efficiency',
    description:
      'JIKER builds the infrastructure and cultivates the builders to turn ideas into real AI-native businesses with 10× efficiency — for industries like finance, energy, e-commerce, education, telecom, and beyond.',
    ctaPrimary: 'Explore Products',
    ctaSecondary: 'Contact Us',
  },

  products: {
    title: 'Our Products',
    description:
      'Two foundations: AI Infra that powers applications, and the AI Builder ecosystem that grows the people who build them.',
    aiInfra: {
      title: 'AI Infra',
      description:
        'The runtime, conversation, routing, and deployment layer behind every AI-native application we ship.',
      looploop: {
        name: 'looploop Family',
        description: 'Agent-powered products for conversation, execution, and application runtime.',
        items: [
          {
            title: 'looploop.chat',
            subtitle: 'Product Definition',
            description:
              'Connects product consultants with innovators to turn fuzzy ideas into executable definitions, roadmaps, and next steps.',
            tags: ['Product Definition', 'Requirement Clarification', 'Feasibility'],
            variant: 'primary',
          },
          {
            title: 'looploop.run',
            subtitle: 'Multi-Agent Collaboration',
            description:
              'Makes agents intelligent teammates, enabling real-time collaboration across users, agents, and machines with secure local execution.',
            tags: ['Multi-Agent Collaboration', 'Local Execution', 'Team Workspace'],
            variant: 'primary',
          },
          {
            title: 'looploop.vip',
            subtitle: 'Agent Runtime',
            description:
              'One-click publish to production-grade infrastructure, so agents run continuously in the real world and deliver value.',
            tags: ['Agent Runtime', 'Real-World Loop', 'Continuous Operations'],
            variant: 'primary',
          },
        ],
      },
      llmpool: {
        name: 'llmpool Family',
        description: 'A unified model pool and routing layer for multi-model AI systems.',
        items: [
          {
            title: 'llmpool.io',
            description:
              'Manage multiple LLMs, route traffic, and optimize cost through one unified API.',
            tags: ['Routing', 'API'],
            variant: 'lavender',
          },
        ],
      },
    },
    talentInfra: {
      title: 'AI Builder',
      description:
        'We select and cultivate AI Builders who turn real business scenarios into working AI products.',
      items: [
        {
          title: 'Geek Academy',
          description:
            'Foundational online training for aspiring AI Builders: concepts, tools, and workflows.',
          tags: ['Online', 'Training'],
          variant: 'red',
        },
        {
          title: 'Geek Future School',
          description:
            'Advanced offline AI Builder ecosystem with real-world projects, mentorship, and community.',
          tags: ['Offline', 'Projects'],
          variant: 'red',
        },
      ],
    },
  },

  stories: {
    title: 'Built with JIKER',
    description:
      'Real-world AI Native Agents and Apps built on looploop and llmpool by our AI Builders.',
    energy: {
      title: 'Energy',
      items: [
        {
          title: 'New Energy Operations Agent',
          category: 'New Energy',
          description:
            'Monitors field equipment, answers operational questions, and drafts maintenance reports for solar and storage fleets.',
          result: 'Maintenance reports: 4 hrs → 20 min',
        },
        {
          title: 'Power Grid Assistant',
          category: 'Power Grid',
          description:
            'Reasons over regulations, fault logs, and equipment manuals to speed up dispatch decisions for grid operators.',
          result: 'Fault analysis prep: 2 days → 3 hrs',
        },
        {
          title: 'Safety Brain Agent',
          category: 'Power Grid',
          description:
            'An LLM + VLM powered agent that inspects work tickets and operation tickets for intelligent safety supervision.',
          result: 'Ticket review: manual sampling → full coverage',
        },
        {
          title: 'Engineering Compliance Agent',
          category: 'Power Grid',
          description:
            'Checks engineering documents, permits, and field practices against regulations to reduce compliance risks.',
          result: '3× more compliance issues caught',
        },
      ],
    },
    ecommerce: {
      title: 'E-commerce',
      items: [
        {
          title: 'E-commerce Service Agent',
          category: 'Customer Service',
          description:
            'An AI assistant for e-commerce customer service teams that handles inquiries, recommends products, and escalates issues.',
          result: 'First response: 45s → 3s',
        },
        {
          title: 'E-commerce CRM Agent',
          category: 'Customer Operations',
          description:
            'An intelligent CRM that analyzes customer conversations, tags intents, and automates follow-ups for online stores.',
          result: '+28% dormant customer reactivation',
        },
      ],
    },
    finance: {
      title: 'Finance',
      items: [
        {
          title: 'Investment Research Agent',
          category: 'Finance',
          description:
            'Aggregates research reports, filings, and market data to help analysts draft compliant investment research faster.',
          result: 'Draft report: 2 days → 40 min',
        },
        {
          title: 'Credit Due Diligence Agent',
          category: 'Finance',
          description:
            'Parses financial statements, credit records, and transaction flows to surface key findings and risks for loan approvals.',
          result: 'Due diligence: 3 weeks → 4 days',
        },
      ],
    },
    education: {
      title: 'Education',
      items: [
        {
          title: 'AI Teaching Assistant',
          category: 'Education',
          description:
            'Helps teaching teams generate lesson plans, grade assignments, and build a shared school knowledge base.',
          result: 'Lesson prep time down 35%',
        },
        {
          title: 'AI Coding Tutor',
          category: 'Education',
          description:
            'Gives learners round-the-clock programming help, code reviews, and personalized learning path advice.',
          result: 'Assignment feedback: 2 days → 10 min',
        },
      ],
    },
    exploring: {
      title: 'Exploring',
      items: [
        {
          title: 'My Twin',
          category: 'AI Digital Twin',
          description:
            'A voice-first AI twin that interviews customers, clarifies requirements, and turns spoken ideas into product briefs.',
          result: 'Interview to brief: 2 weeks → 2 days',
        },
      ],
    },
  },

  mission: {
    title: 'Our Services',
    description:
      'JIKER builds AI Infra, cultivates AI Builders, and creates industry applications to help every talent create greater value.',
    items: [
      {
        title: 'Build AI Infra',
        description:
          'We build the foundational tools—conversation, execution, model routing, and deployment—that every AI-native application needs.',
      },
      {
        title: 'Cultivate AI Builders',
        description:
          'Geek Academy and Geek Future School train AI Builders who turn business scenarios into working products using our infra.',
      },
      {
        title: 'Create Applications',
        description:
          'With infra and AI Builders in place, we continuously ship AI Native Agents and Apps for industries like finance, energy, e-commerce, education, and telecom.',
      },
    ],
  },

  about: {
    title: 'Jikers.',
    subtitle: 'No Limits, Infinite Exploration.',
    description:
      'A group of explorers challenging the status quo, pursuing extreme performance, and sharing what we build with confidence.',
    items: [
      {
        title: 'Challenge the Status Quo',
        description:
          'Pioneering spirit and democratized innovation. We explore new paths with AI instead of accepting old software.',
      },
      {
        title: 'Pursuit of Extreme Performance',
        description:
          'High quality, purity, and continuous evolution. We refine until it works, then refine again until it is exceptional.',
      },
      {
        title: 'Confidence to Share',
        description:
          'Liberating learning, close partnership, and social value. We grow together as a community of builders.',
      },
    ],
  },

  cta: {
    title: 'Ready to build with AI?',
    description:
      'Tell us about your industry scenario, or join Future School to become an AI Builder.',
    primary: 'Contact Us',
  },

  footer: {
    description:
      'JIKER builds AI Infra, cultivates AI Builders, and ships AI-native applications for industries like finance, energy, e-commerce, education, telecom, and beyond.',
    productsTitle: 'Products',
    connectTitle: 'Connect',
    email: "Email",
    github: 'GitHub',
    twitter: 'Twitter / X',
    copyright: '© {year} JIKER. All rights reserved.',
    tagline: 'Make talent more valuable',
  },
};
